import React, {useEffect, useState} from 'react';
import Box from '@mui/material/Box';
import {GenericConfigurationForm, FormItem, validateFormData} from '../../../../components/GenericConfigurationForm';
interface PeakParametersProps {
    formData: any;
    /**
     * I don't like how much this can change. Provide a more narrow function
     * if possible
     */
    setFormData: (data: any) => void;
    /**
     * When the formData changes, the chip parameters are validated. This
     * function is called with the result of that validation.
     */
    setValidForm: (isValid: boolean) => void;
}

const items: Readonly<FormItem>[] = [
    {
        name: 'peak_calling_qvalue',
        humanReadableName: 'Peak calling q-value',
        description: 'Peak calling q-value threshold for filtering peaks.',
        longDescription:
            'The q-value (minimum FDR) cutoff to call significant regions. Default is 0.05. For broad marks, you can try 0.01 as the cutoff. The q-values are calculated from p-values using the Benjamini-Hochberg procedure.',
        numbers: {
            min: 0,
            max: 1,
            step: 0.01
        }
    },
    {
        name: 'peak_calling_type',
        humanReadableName: 'Peak calling type',
        description: 'Peak detection mode controls whether narrow or broad peaks are called.',
        longDescription:
            'Determines the MACS2 peak calling mode. Narrow peaks detects sharp, well-defined enrichment signals typical of transcription factor binding sites. Broad peaks detects wider enriched regions typical of certain histone modifications. Currently narrow peaks is supported, with broad peaks in development.',
        selects: [{value: 'narrow_peaks', label: 'Narrow peaks'}]
    },
    {
        name: 'peak_annotation_tss_region_upstream',
        humanReadableName: 'Transcription Start Site (TSS) region upstream',
        description:
            'Upstream distance from TSS for peak annotation. Together with downstream distance, defines a TSS region of [-upstream, downstream] around the TSS.',
        longDescription:
            'Distance upstream from the transcription start site (TSS) to consider for peak annotation. Setting TSS region to [-3000, 3000] means that any peak located within 3000 base pairs upstream or downstream of a gene\'s TSS will be considered part of its promoter region and labeled accordingly in the annotation output. When annotating peaks, a genomic feature is assigned to each peak, such as Promoter, Exon, Intron, UTR, or Intergenic regions. If a peak falls within the specified tssRegion window, it will be labeled as "Promoter". This parameter enables researchers to customize the definition of a promoter based on their biological context. Note that the tssRegion parameter does not filter the annotated peak file; it simply assigns the appropriate label based on the peak\'s location relative to the TSS region defined by this parameter.',
        numbers: {
            min: 0,
            max: 10000,
            step: 1
        }
    },
    {
        name: 'peak_annotation_tss_region_downstream',
        humanReadableName: 'Transcription Start Site (TSS) region downstream',
        description:
            'Downstream distance from TSS for peak annotation. Together with upstream distance, defines a TSS region of [-upstream, downstream] around the TSS.',
        longDescription:
            'Distance downstream from the transcription start site (TSS) to consider for peak annotation. Setting TSS region to [-3000, 3000] means that any peak located within 3000 base pairs upstream or downstream of a gene\'s TSS will be considered part of its promoter region and labeled accordingly in the annotation output. When annotating peaks, a genomic feature is assigned to each peak, such as Promoter, Exon, Intron, UTR, or Intergenic regions. If a peak falls within the specified tssRegion window, it will be labeled as "Promoter". This parameter enables researchers to customize the definition of a promoter based on their biological context. Note that the tssRegion parameter does not filter the annotated peak file; it simply assigns the appropriate label based on the peak\'s location relative to the TSS region defined by this parameter.',
        numbers: {
            min: 0,
            max: 10000,
            step: 1
        }
    },
    {
        name: 'peak_annotation_level',
        humanReadableName: 'Peak annotation level',
        description:
            'Control the level at which gene features are considered during annotation, impacting the level of detail provided in the output.',
        longDescription:
            '"transcript" means the annotation focuses on individual transcripts within a gene. If a peak overlaps with a specific Exon, Intron, or UTR of a particular transcript, the output will indicate this; "gene" will associate peaks with the overall gene entity, providing gene-level information rather than transcript-specific details',
        selects: [
            {value: 'transcript', label: 'Transcript'},
            {value: 'gene', label: 'Gene'}
        ]
    },
    {
        name: 'peak_annotation_overlap',
        humanReadableName: 'Peak annotation overlap',
        description: 'Overlap type for peak annotation.',
        longDescription:
            'Dictates how overlapping genomic features are handled during peak annotation. When a peak overlaps multiple genomic regions, this determines the primary annotation. "TSS" (Transcription Start Site) annotates with the gene closest to, or overlapping with, the peak. "All" reports all genes within a certain distance, regardless of TSS overlap.',
        selects: [
            {value: 'tss', label: 'TSS'},
            {value: 'all', label: 'All'}
        ]
    },
    {
        name: 'peak_annotation_genomic_annotation_priority',
        humanReadableName: 'Genomic annotation priority',
        description: 'Define the order of priority for genomic annotations when multiple annotations overlap with a peak.',
        longDescription:
            'Sometimes, a single ChIP seq peak might overlap with multiple genomic features, e.g., a peak could be within an exon, but also near a Transcription Start Site (TSS). To ensure a consistent and meaningful annotation, the following list decides which genomic feature to assign to the peak in such overlapping cases.',
        list_items: [
            {value: 'Promoter', label: 'Promoter'},
            {value: '5UTR', label: '5UTR'},
            {value: '3UTR', label: '3UTR'},
            {value: 'Exon', label: 'Exon'},
            {value: 'Intron', label: 'Intron'},
            {value: 'Downstream', label: 'Downstream'},
            {value: 'Intergenic', label: 'Intergenic'}
        ]
    }
];

export const PeakParameters = ({formData, setFormData, setValidForm}: PeakParametersProps) => {
    const handleInputChange = (values: Record<string, any>) => {
        setFormData((prevData: any) => ({
            ...prevData,
            default_chip_analysis: {
                ...prevData.default_chip_analysis,
                ...values
            }
        }));
    };

    const [errors, setErrors] = useState<Record<string, string>>({});

    useEffect(() => {
        const newErrors = validateFormData(formData.default_chip_analysis, items);
        setErrors(newErrors);
        setValidForm(Object.keys(newErrors).length === 0);
    }, [formData, setValidForm]);

    return (
        <Box
            p={3}
            my={1}
            sx={{borderRadius: '10px', bgcolor: 'surface-container-lowest', display: 'flex', flexDirection: 'column', gap: 3}}>
            <GenericConfigurationForm
                items={items}
                itemValues={formData.default_chip_analysis}
                setItemValues={handleInputChange}
                itemErrors={errors}
            />
        </Box>
    );
};
